// Stage ⓪ — chuyển thể tiểu thuyết thành khung xương kịch bản (đợt 260812).
// Endpoints: /api/v1/admin/film/adaptation/*
import { apiClient } from './client';
import type {
  AdaptationProgress, EpisodeScriptRecord, FilmGlossaryTerm, ManualSaveResult, FilmGlossaryType, FilmOutline,
  GateStepKey, SeasonReaderGate, SeasonReaderViewResponse, LocalizeMode, LocalizePolicy, LocalizeView, OutlinePatchOp, StepGate, WorldSettingLite, OutlineRecord, EpisodeSplitOp, EpisodeSplitResponse, SplitCandidatesResponse, PatchPreviewResponse, PatchResponse, ProbeReport, RawChapter, ScanEstimate, RawChapterOpOutcome, RawChapterReloadPreview, RawChapterSplitCandidates, RawChapterVersionMeta,
  LocalizeOp, LocalizePreviewResponse, LocalizePatchResponse, LocalizeVersionMeta, LocalizeVersionFull,
  VisualIdentitySummary, VisualRunBody, VisualEditBody, VisualEditResult,
  VisualExtrasSummary, VisualExtrasRunBody, VisualExtrasEditBody, VisualExtrasEditResult,
} from '../../types/adaptation';
import type { ConsequencePreview, HandoffBatchPreview, HandoffPlanPreview, NewStoryRunInputs, WorkbenchView } from '../../types/adaptationWorkbench';

const BASE = '/api/v1/admin/film/adaptation';

/** Trả lời của endpoint chạy NỀN (202) — kết quả xem bằng cách poll lại dữ liệu. */
export interface AdaptationBgStarted {
  started: boolean;
  jobId?: string;
  message: string;
}

export interface PutPolicyResult {
  changed: boolean;
  policy: LocalizePolicy;
  resultStale: boolean;
  warnings: Array<{ code: string; message: string; kind?: string }>;
  lockedEntityCount: number;
}

export interface AcceptWorldResult {
  worldSetting: WorldSettingLite;
  eraChanged: boolean;
  warnings: Array<{ code: string; message: string; kind?: string }>;
}

export type OverviewMode = 'full' | 'force' | 'merge';
/** Phản hồi chốt: `modifiedAt` mới (để các thao tác sau dùng làm mốc) · `approved` = biên nhận chốt khớp. */
export interface ApproveResult { status: string; violations?: unknown[]; modifiedAt?: string; approved?: boolean }
/** 261023 E3-P3c1 — body `POST …/new-story-run`: đầu vào D6 (mặc định ở BE) HOẶC `resumeRunId` ("Chạy tiếp" trước khi lưu khung xương, dùng lại đầu vào cũ). */
export type NewStoryRunStartBody = Partial<NewStoryRunInputs> & { resumeRunId?: string; /** 261023 E3-P3c2a — số «tối đa» người đã thấy ở khung Bắt đầu (BẮT BUỘC khi bắt đầu mới; lệch ⇒ 409 `ESTIMATE_CHANGED`). Không dùng với `resumeRunId`. */ expectedMaxUsd?: number; /** 261025 E3-P3d3 — «Chạy tiếp, để lỗi xem sau» ở cổng ① quét quyển (chỉ đi với `resumeRunId`). */ acceptVolumeIssues?: boolean; /** 261023 E3-P3h5b — BE tự lấy ước làm trần (BE cũ bỏ qua và dùng `expectedMaxUsd`, nên vẫn gửi cả hai). */ budgetMode?: 'SERVER_ESTIMATE' };
/** 261023 E3-P3c2a — `GET …/new-story-run/estimate/:storyId` (CHÉP từ BE `NewStoryEstimate`). Số `null` = chưa biết giá (không bao giờ hiện $0). */
export interface NewStoryEstimateUnit { step: string; unitKey: string; calls: number; perCallMaxUsd: number | null; model: string; chars?: number }
export interface NewStoryEstimate {
  rateVersion?: string;
  chang1: { maxUsd: number | null; typicalUsd: number | null; typicalMissingSteps?: string[]; assumption?: string; units: NewStoryEstimateUnit[]; unknownReasons?: string[] };
  /** BE trả `deferred:false` từ 3c2b; FE không dựa vào trường này. */
  chang2: { deferred: boolean };
  /** 261023 E3-P3c2b (G7) — ước thô CẢ CHUỖI (không phải trần). Z = chang1MaxUsd + chang2BaseUsd + chang2PerEpisodeUsd × số tập. Số nào `null` ⇒ ẩn dòng. */
  chainRough?: NewStoryChainRough | null;
}
export interface NewStoryChainRough { chapters: number; ratio: number; defaultEpisodes: number; chang1MaxUsd: number | null; chang2BaseUsd: number | null; chang2PerEpisodeUsd: number | null }
/** 261023 E3-P3c2a — `POST …/new-story-run/budget-cap`. */
export interface NewStoryRaiseCapBody { storyId: string; runId: string; expectedRevision: number; newCapUsd: number }
/** 261023 E3-P3h5b — `POST …/new-story-run/continue-auto-budget`: KHÔNG nhận số tiền nào; kết quả KHÔNG có số tiền. */
export interface NewStoryContinueBody { storyId: string; runId: string; expectedRevision: number; requestId: string }
export interface NewStoryContinueResult { resumed: boolean; raised: boolean; revision: number; replay?: boolean; step?: string; resumeError?: { code: string } }
export interface NewStoryRaiseCapResult { capUsd: number; revision: number; resumed: boolean; step?: string; resumeError?: { code: string; message: string } }
/** 261025 E3-P3d3 — `jobId` null + `waiting:'REVIEW_GATE'` = «Chạy tiếp» nhưng không có gì để chạy lại (quyển chưa sạch giữ nguyên) ⇒ chờ người sửa chương / chọn «để lỗi xem sau». */
export interface NewStoryRunStarted { started: boolean; runId: string; step: 'SCAN' | 'SKELETON'; jobId: string | null; waiting?: 'REVIEW_GATE'; message?: string }
export interface AppendRunStarted { started: boolean; runId?: string; step?: string; fromOrderIndex?: number; localizeSkipped?: boolean; message?: string }
export interface AppendRunCancelled { cancelled: boolean; restored?: boolean; discardedVersions?: number[]; warning?: string }
export interface AppendUnusedItem { id: string; kind: 'character' | 'location'; status: 'SAFE' | 'REFERENCED' | 'UNKNOWN'; referenceKinds: string[] }
/** Dấu vết lần bỏ gần nhất (BE ghi cùng CAS). `postModifiedAt` = mốc `modifiedAt` ngay sau lúc bỏ: khác `modifiedAt` hiện tại ⇒ đã có ghi khác ⇒ không hoàn tác được. */
export interface AppendUnusedRemoval { at: string; by: string; ids: string[]; statusBefore: string; preVersionId: string; preVersionNo: number; postModifiedAt: string }
export interface AppendUnusedPreview { available: boolean; reason?: string; runId?: string; modifiedAt?: string; candidates?: AppendUnusedItem[]; earlierOrphans?: AppendUnusedItem[]; overviewPresent: boolean; removal?: AppendUnusedRemoval | null }

export const adaptationApi = {
  // ── Nạp truyện ────────────────────────────────────────────────────────
  /** Dò một link truyện: lấy được bao nhiêu phần nội dung, có bị khoá không. */
  probe: (url: string) =>
    apiClient.post<ProbeReport>(`${BASE}/probe`, { url }),

  /** `autoScan` (261005 E4-P3): nạp xong tự quét; kết quả có `scanJobId` / `autoScanSkipped` ở `detail.result`. */
  crawl: (storyId: string, url: string, maxChapters?: number, autoScan?: boolean, startChapter?: number) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/crawl`, { storyId, url, maxChapters, autoScan, startChapter }),

  /** Ước giá quét (261005 E4-P3): `chapters` (ước theo N) hoặc `storyId` (tự tính từ quyển sẽ quét). Không AI. */
  scanEstimate: (q: { chapters?: number; storyId?: string }) =>
    apiClient.get<ScanEstimate>(`${BASE}/scan-estimate`, { params: q }),

  crawlStatus: (storyId: string) =>
    apiClient.get(`${BASE}/crawl/${storyId}/status`),

  /** Trạng thái từng bước — UI dùng để biết bước nào mở được. */
  progress: (storyId: string) =>
    apiClient.get<AdaptationProgress>(`${BASE}/progress/${storyId}`),

  rawChapters: (storyId: string) =>
    apiClient.get<RawChapter[]>(`${BASE}/raw-chapters/${storyId}`),

  /** Thêm/sửa chương thô bằng tay — đường vào thứ hai bên cạnh crawl. */
  saveRawChapter: (storyId: string, data: { id?: string; name?: string; content: string }) =>
    apiClient.post<RawChapter>(`${BASE}/raw-chapters`, { storyId, ...data }),

  /** Sửa chương có sẵn (khoá M + phiên bản + hạ cờ). `dryRun` ⇒ chỉ trả `impact`. */
  editRawChapter: (storyId: string, id: string, data: { name?: string; content: string; expectedModifiedAt?: string; dryRun?: boolean }) =>
    apiClient.post<RawChapterOpOutcome>(`${BASE}/raw-chapters`, { storyId, id, ...data }),

  /** Xoá chương + dồn số. `dryRun` ⇒ chỉ trả `impact`. */
  deleteRawChapter: (id: string, opts: { expectedModifiedAt?: string; dryRun?: boolean } = {}) =>
    apiClient.delete<RawChapterOpOutcome>(`${BASE}/raw-chapters/${id}`, { params: opts.dryRun ? { dryRun: 1 } : undefined, data: opts.expectedModifiedAt ? { expectedModifiedAt: opts.expectedModifiedAt } : undefined }),

  rawChapterReloadPreview: (id: string) =>
    apiClient.post<RawChapterReloadPreview>(`${BASE}/raw-chapters/${id}/reload-preview`, {}),
  rawChapterReload: (id: string, data: { content: string; contentSha: string; expectedModifiedAt?: string; dryRun?: boolean }) =>
    apiClient.post<RawChapterOpOutcome>(`${BASE}/raw-chapters/${id}/reload`, data),
  rawChapterSplitCandidates: (id: string) =>
    apiClient.get<RawChapterSplitCandidates>(`${BASE}/raw-chapters/${id}/split-candidates`),
  rawChapterSplit: (id: string, data: { offset: number; expectedModifiedAt?: string; dryRun?: boolean }) =>
    apiClient.post<RawChapterOpOutcome>(`${BASE}/raw-chapters/${id}/split`, data),
  rawChapterMergeNext: (id: string, data: { expectedModifiedAt?: string; dryRun?: boolean } = {}) =>
    apiClient.post<RawChapterOpOutcome>(`${BASE}/raw-chapters/${id}/merge-next`, data),
  rawChapterAcceptFinding: (id: string, code: string, accepted = true) =>
    apiClient.post(`${BASE}/raw-chapters/${id}/accept-finding`, { code, accepted }),
  rawChapterVersions: (storyId: string) =>
    apiClient.get<RawChapterVersionMeta[]>(`${BASE}/raw-chapters/${storyId}/versions`),
  rawChapterVersionRestore: (versionId: string, data: { dryRun?: boolean } = {}) =>
    apiClient.post<RawChapterOpOutcome>(`${BASE}/raw-chapter-versions/${versionId}/restore`, data),

  /** 261005 E1-P1 — API tóm tắt bàn làm việc (chỉ đọc). */
  workbench: (storyId: string) =>
    apiClient.get<WorkbenchView>(`${BASE}/workbench/${storyId}`),

  /** Xem trước "Đẩy sang Chapters" (không ghi). 409: OUTLINE_MISSING / OUTLINE_NOT_APPROVED / NO_DRAFT_SCRIPTS. */
  handoffPlan: (storyId: string) =>
    apiClient.get<HandoffPlanPreview>(`${BASE}/workbench/${storyId}/handoff-plan`),

  handoff: (storyId: string) =>
    apiClient.post(`${BASE}/handoff`, { storyId }),

  /**
   * 261025 E3-P3f — «Duyệt & đẩy N tập». `preview:true` ⇒ 200 kế hoạch từng tập (không ghi). Thật ⇒ 202 `{ jobId }` (việc nền; kết quả đọc ở `workbench.handoffBatch`).
   * 409 chỉ cho lỗi cả lô: `SCRIPT_CHANGED` (+`episodes`) · `HANDOFF_RUNNING` (+`jobId`) · `SCRIPT_JOB_RUNNING` · `JOB_RUNNING` · `OUTLINE_*` · `NO_DRAFT_SCRIPTS` · `EPISODES_STALE` · `APPEND_RUN_ACTIVE` · `SEASON_READER_*`.
   */
  handoffBatchPreview: (storyId: string, episodes: Array<{ episode: number; contentSha?: string }>) =>
    apiClient.post<HandoffBatchPreview>(`${BASE}/handoff-batch`, { storyId, episodes, preview: true }),
  handoffBatchStart: (storyId: string, episodes: Array<{ episode: number; contentSha: string }>) =>
    apiClient.post<{ started: boolean; jobId: string; message?: string }>(`${BASE}/handoff-batch`, { storyId, episodes }),
  /** Đặt cờ huỷ: tập đã đẩy giữ nguyên, phần còn lại `cancelled`. 404 `HANDOFF_JOB_NOT_FOUND` · 409 `HANDOFF_NOT_RUNNING`. */
  handoffBatchCancel: (jobId: string) =>
    apiClient.post<{ cancelRequested: boolean }>(`${BASE}/handoff-batch/${jobId}/cancel`, {}),

  // ── Xử lý phần mới (261005 E4-P4b) ───────────────────────────────────
  appendRunStart: (storyId: string) =>
    apiClient.post<AppendRunStarted>(`${BASE}/append-run`, { storyId }),
  /**
   * `expectedModifiedAt` (261015 P4c-1, Q1): mốc `modifiedAt` của bản người đang xem — BẮT BUỘC ở "Duyệt & chạy tiếp" (STOP 1, mốc ②) VÀ "Chốt dàn tập & chạy tiếp" (STOP 2, mốc dàn tập); thiếu ⇒ 400 `EXPECTED_MODIFIED_AT_REQUIRED`, lệch ⇒ 409 `OUTLINE_CHANGED`.
   * Chỉ "Chạy tiếp" sau gián đoạn (resume) BE bỏ qua ⇒ không truyền.
   * `expectedMaxUsd` (261023 E3-P3c2b): chỉ chuỗi NEW_STORY ở STOP 1 — thiếu ⇒ 400 `EXPECTED_MAX_REQUIRED`; lệch ⇒ 409 `ESTIMATE_CHANGED` (+`maxUsd`) · `ESTIMATE_UNKNOWN` (+`unknownReasons`) · `RUN_ADVANCED` · `CHAIN_MOVED` · `OUTLINE_CHANGED` · `BUDGET_BLOCKED`.
   */
  appendRunContinue: (storyId: string, expectedModifiedAt?: string, expectedMaxUsd?: number, budgetMode?: 'SERVER_ESTIMATE') =>
    apiClient.post<AppendRunStarted>(`${BASE}/append-run/continue`, {
      storyId, ...(expectedModifiedAt ? { expectedModifiedAt } : {}),
      // 261023 E3-P3c2b: số «tối đa» Chặng 2 người đã thấy ở STOP 1 (BẮT BUỘC với chuỗi truyện mới; lệch ⇒ 409 `ESTIMATE_CHANGED`). Gửi SỐ THÔ của BE, không phải số đã làm tròn để hiển thị.
      ...(typeof expectedMaxUsd === 'number' && Number.isFinite(expectedMaxUsd) ? { expectedMaxUsd } : {}),
      // 261023 E3-P3h5b: BE mới tự lấy ước Chặng 2 làm trần (bỏ qua `expectedMaxUsd`); BE cũ bỏ qua khoá này và vẫn so `expectedMaxUsd` ⇒ gửi cả hai là an toàn ở cả hai phía.
      ...(budgetMode ? { budgetMode } : {}),
    }),
  appendRunCancel: (storyId: string) =>
    apiClient.post<AppendRunCancelled>(`${BASE}/append-run/cancel`, { storyId }),
  appendUnusedPreview: (storyId: string) =>
    apiClient.get<AppendUnusedPreview>(`${BASE}/append-run/unused-preview/${storyId}`),
  appendUnusedRemove: (body: { storyId: string; runId: string; ids: string[]; expectedModifiedAt: string }) =>
    apiClient.post<{ removed: number; modifiedAt: string }>(`${BASE}/append-run/remove-unused`, body),
  appendUnusedUndo: (body: { storyId: string; runId: string; expectedModifiedAt: string }) =>
    apiClient.post<{ undone: boolean; modifiedAt: string }>(`${BASE}/append-run/remove-unused/undo`, body),
  /** 261023 E3-P3c1 — "Bắt đầu" cho truyện mới (chưa có khung xương) / "Chạy tiếp" trước khi lưu khung xương (`resumeRunId`). */
  newStoryRunStart: (storyId: string, body: NewStoryRunStartBody = {}) =>
    apiClient.post<NewStoryRunStarted>(`${BASE}/new-story-run`, { storyId, ...body }),
  /** 261023 E3-P3c2a — ước «tối đa» chặng 1 (không AI). */
  newStoryRunEstimate: (storyId: string) =>
    apiClient.get<NewStoryEstimate>(`${BASE}/new-story-run/estimate/${storyId}`),
  /**
   * 261025 3d1 (D8) — «Chạy tiếp, để lỗi xem sau» ở `WAIT_REVIEW_GATE`: audit + sang SPLIT trong một CAS, KHÔNG gọi AI.
   * `expectedModifiedAt` BẮT BUỘC (mốc người đang xem): thiếu ⇒ 400 `EXPECTED_MODIFIED_AT_REQUIRED`; lệch ⇒ 409 `OUTLINE_CHANGED`; không còn chờ cổng ⇒ 409 `NOT_WAITING_GATE`.
   */
  newStoryRunReviewGateContinue: (body: { storyId: string; runId: string; step: string; expectedModifiedAt: string }) =>
    apiClient.post<{ started: boolean; runId: string; step: string }>(`${BASE}/new-story-run/review-gate/continue`, body),
  /** 261023 E3-P3c2a — nâng trần tiền và chạy tiếp (CAS theo `expectedRevision`). */
  newStoryRunRaiseCap: (body: NewStoryRaiseCapBody) =>
    apiClient.post<NewStoryRaiseCapResult>(`${BASE}/new-story-run/budget-cap`, body),
  /** 261023 E3-P3h5b — «Chạy tiếp» MỘT bấm: BE tự tính trần (không nhận số tiền), idempotent theo `requestId`. Chỉ gọi khi workbench có `continuation`. */
  newStoryRunContinueAutoBudget: (body: NewStoryContinueBody) =>
    apiClient.post<NewStoryContinueResult>(`${BASE}/new-story-run/continue-auto-budget`, body),

  // ── Quét truyện ───────────────────────────────────────────────────────
  scan: (storyId: string, force?: boolean) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/scan`, { storyId, force }),

  scanResult: (storyId: string) =>
    apiClient.get(`${BASE}/scan/${storyId}`),

  // ── Khung xương ───────────────────────────────────────────────────────
  buildSkeleton: (storyId: string, params: { genre: string; minutesPerEpisode?: number; adaptMode?: string; episodes?: number }) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/outline`, { storyId, ...params }),

  getOutline: (storyId: string) =>
    apiClient.get<OutlineRecord | null>(`${BASE}/outline/${storyId}`),

  approveEpisodePlan: (storyId: string, expectedModifiedAt: string) =>
    apiClient.post(`${BASE}/outline/${storyId}/episode-plan/approve`, { expectedModifiedAt }),
  unapproveEpisodePlan: (storyId: string, expectedModifiedAt: string) =>
    apiClient.post(`${BASE}/outline/${storyId}/episode-plan/unapprove`, { expectedModifiedAt }),

  /**
   * Chia tập. `body` mặc định `{}` = chế độ Theo chương như cũ; range ⇒ `{ sourceMode:'range' }`
   * (xem `buildEpisodesBody`). 409 trả TRƯỚC khi tạo job.
   */
  buildEpisodes: (storyId: string, body: { sourceMode?: 'range'; force?: boolean } = {}) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/outline/${storyId}/episodes`, body),

  /** Viết kịch bản các tập (nền). Bỏ qua tập đã viết trừ khi `force`. */
  writeScripts: (storyId: string, opts: { from?: number; to?: number; force?: boolean; expectedFingerprint?: string } = {}) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/scripts`, { storyId, ...opts }),

  /** 3h4c — hộp hậu quả CHỈ ĐỌC (GET, 0 ghi / 0 AI). 404 `OUTLINE_MISSING`. `base.outlineModifiedAt` ⇒ `expectedModifiedAt` của `unapprove`. */
  unapproveImpact: (storyId: string) => apiClient.get<ConsequencePreview>(`${BASE}/outline/${storyId}/unapprove-impact`),
  /** 3h4c — xoá một từ vựng. 404 `TERM_NOT_FOUND`. `base.outlineModifiedAt` ⇒ `expectedModifiedAt` của PATCH ops `glossaryDelete`. */
  glossaryDeleteImpact: (storyId: string, glossaryId: string) => apiClient.get<ConsequencePreview>(`${BASE}/localize/${storyId}/glossary/${glossaryId}/delete-impact`),
  /** 3h4c — viết lại MỘT tập. `base.fingerprint` ⇒ `expectedFingerprint` của `writeScripts` (lệch ⇒ 409 `IMPACT_CHANGED`). 400 `BAD_EPISODE`. */
  rewriteImpact: (storyId: string, episode: number) => apiClient.get<ConsequencePreview>(`${BASE}/scripts/${storyId}/${episode}/rewrite-impact`),

  /** 261005 E4-P1 — sửa tay: lưu NGUYÊN tập (không AI). 400 cấu trúc · 409 `SCRIPT_CHANGED`/`JOB_RUNNING` · 422 `MANUAL_LINT_ERROR` (+ `violations`). */
  saveManualScript: (storyId: string, episode: number, body: { script: NonNullable<EpisodeScriptRecord['script']>; expectedModifiedAt: string }) =>
    apiClient.put<ManualSaveResult>(`${BASE}/scripts/${storyId}/${episode}/manual`, body),

  /**
   * 261023 E3-P3b2 — «Chấm lại bằng AI» MỘT tập: giám khảo C2 chấm lại bản đang lưu, KHÔNG sửa chữ nào. Trả job nền (202).
   * 404 `EPISODE_NOT_FOUND` · 409 `NOT_DRAFT` / `SCRIPT_CHANGED` / `WRITER_UNKNOWN` / `JOB_RUNNING` / cổng khung xương-dàn tập — chữ tiếng Việt trong `message`.
   */
  rejudgeC2: (storyId: string, episode: number, expectedModifiedAt: string) =>
    apiClient.post<{ started: boolean; jobId: string }>(`${BASE}/scripts/${storyId}/${episode}/rejudge-c2`, { expectedModifiedAt }),

  /**
   * 261023 E3-P3j2b-2 — «Để máy sửa N tập»: server TỰ chọn tập đủ điều kiện (`episodes` chỉ để «sửa riêng tập này», phải ⊆ tập đủ điều kiện). `requestId` (8–100 ký tự `[A-Za-z0-9_-]`) idempotent:
   * gửi lại cùng id ⇒ `replay:true` + đúng job cũ, không mở job / không giữ chỗ thêm. 202. Lỗi `{message, code}`: 400 `EPISODES_NOT_ELIGIBLE` · 404 `AUTO_REPAIR_DISABLED` · 409 `NO_ELIGIBLE_EPISODES` / `JOB_RUNNING` / `APPEND_RUN_ACTIVE` / `ESTIMATE_UNKNOWN` / `OUTLINE_NOT_APPROVED`…
   */
  autoRepairStart: (body: { storyId: string; requestId: string; episodes?: number[] }) =>
    apiClient.post<{ started: boolean; jobId: string; replay: boolean; episodes: number[] }>(`${BASE}/auto-repair/start`, body),
  /** «Dừng» lần sửa đang chạy: dừng êm giữa hai lời gọi, giữ bản đang lưu. 404 `AUTO_REPAIR_DISABLED` · 409 `AUTO_REPAIR_NOT_RUNNING`. */
  autoRepairStop: (storyId: string) =>
    apiClient.post<{ cancelRequested: boolean; jobId: string }>(`${BASE}/auto-repair/stop`, { storyId }),

  /** Danh sách kịch bản đã viết — để biết tập nào xong, tập nào chưa. */
  scripts: (storyId: string) =>
    apiClient.get<EpisodeScriptRecord[]>(`${BASE}/scripts/${storyId}`),

  /** Lint lại bản đang lưu + vòng sửa (nền). Dùng sau khi sửa tay. */
  repair: (storyId: string) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/outline/${storyId}/repair`, {}),

  // ── Cổng duyệt ────────────────────────────────────────────────────────
  /** `expectedModifiedAt` (ISO) tuỳ chọn: lệch ⇒ 409 `OUTLINE_CHANGED`. */
  patch: (storyId: string, ops: OutlinePatchOp[], expectedModifiedAt?: string) =>
    apiClient.patch<PatchResponse>(`${BASE}/outline/${storyId}`, { ops, ...(expectedModifiedAt ? { expectedModifiedAt } : {}) }),

  /** 261005 E3-P1a — xem thử lượt sửa ② (KHÔNG ghi, KHÔNG AI): kết quả + `impact` tập đã viết + `baseModifiedAt`. */
  patchPreview: (storyId: string, ops: OutlinePatchOp[]) =>
    apiClient.post<PatchPreviewResponse>(`${BASE}/outline/${storyId}/patch-preview`, { ops }),

  /** 261005 E3-P2a — xem thử lệnh sửa tay ④ (KHÔNG ghi, KHÔNG AI). */
  splitPreview: (storyId: string, ops: EpisodeSplitOp[]) =>
    apiClient.post<EpisodeSplitResponse>(`${BASE}/outline/${storyId}/split-preview`, { ops }),

  /** 261005 E3-P2a — ghi lệnh sửa tay ④; `expectedModifiedAt` lệch ⇒ 409 `OUTLINE_CHANGED`. Giữ nguyên trạng thái duyệt. */
  splitPatch: (storyId: string, ops: EpisodeSplitOp[], expectedModifiedAt?: string) =>
    apiClient.patch<EpisodeSplitResponse>(`${BASE}/outline/${storyId}/split`, { ops, ...(expectedModifiedAt ? { expectedModifiedAt } : {}) }),

  /** 261005 E3-P2a — ứng viên điểm cắt của một chương (chỉ truyện chia theo đoạn nguồn) + nội dung chương. */
  splitCandidates: (storyId: string, orderIndex: number) =>
    apiClient.get<SplitCandidatesResponse>(`${BASE}/outline/${storyId}/split-candidates`, { params: { orderIndex } }),

  /**
   * 261015 P4c-1 (D6/Q17): `expectedModifiedAt` BẮT BUỘC (mốc bản người đang xem; thiếu ⇒ 400). 409: `OUTLINE_CHANGED` · `OVERVIEW_MISSING|INCOMPLETE|SOURCE_STALE|SKELETON_CHANGED|REFERENCE_BROKEN|UNVERIFIED`
   * (kèm `details.issues`). Lỗi lint ⇒ 400 `{ message, violations }`.
   */
  approve: (storyId: string, expectedModifiedAt: string) =>
    apiClient.post<ApproveResult>(`${BASE}/outline/${storyId}/approve`, { expectedModifiedAt }),

  /** `expectedModifiedAt` BẮT BUỘC như `approve`. Bỏ chốt xoá luôn biên nhận chốt tổng quan. */
  unapprove: (storyId: string, expectedModifiedAt: string) =>
    apiClient.post<{ status: string; modifiedAt?: string }>(`${BASE}/outline/${storyId}/unapprove`, { expectedModifiedAt }),

  // ── 261015 P4c-1 — "Tổng quan truyện" ───────────────────────────────────
  /**
   * Tạo tổng quan (NỀN 202, TỐN AI). `mode`: `full` = tạo/tạo lại (chỉ đọc quyển cần) · `force` = đọc lại TOÀN BỘ · `merge` = chỉ gộp lại trên ý đã lưu (không đọc nguyên tác).
   * `expectedModifiedAt` = bản người đang xem (lệch ⇒ 409 `OUTLINE_CHANGED`). 409 `JOB_RUNNING` (+`busy`) · `OVERVIEW_MISSING|SOURCE_STALE` (merge) · 400 `OVERVIEW_SOURCE_TOO_LONG` (+`details {chars,max}`) · `OVERVIEW_SOURCE_EMPTY` · `INVALID_OVERVIEW_MODE`.
   */
  buildOverview: (storyId: string, body: { mode: OverviewMode; expectedModifiedAt?: string }) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/outline/${storyId}/overview`, body),

  // ── Bản địa hoá sang phương Tây (Epic 8) ─────────────────────────────
  /** Bảng ánh xạ gốc↔Tây + glossary + world_setting đích — nguồn cho panel 2. */
  getLocalize: (storyId: string) =>
    apiClient.get<LocalizeView>(`${BASE}/localize/${storyId}`),

  /**
   * Pass localize đầy đủ (tự chạy trích quan hệ nếu thiếu) — nền, trả jobId.
   * `redo` (vá 260907): mặc định bồi chỗ trống; `true` = làm lại từ đầu, đặt lại cả bối cảnh đích.
   */
  runLocalize: (storyId: string, opts?: { redo?: boolean }) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/localize`, { storyId, redo: opts?.redo === true }),

  /** E3-P1: ghim chính sách localize (không AI, không job). 409 `LOCALIZE_POLICY_LOCKED` ⇒ xác nhận rồi gửi lại `force:true`. */
  putPolicy: (storyId: string, body: { mode: LocalizeMode; targetCulture?: string; force?: boolean }) =>
    apiClient.put<PutPolicyResult>(`${BASE}/localize/${storyId}/policy`, body),

  /** E3-P1: duyệt bối cảnh KEEP/MAP đã đề xuất → `source_stories.world_setting`. */
  acceptWorld: (storyId: string, body: { pinned?: boolean; force?: boolean } = {}) =>
    apiClient.post<AcceptWorldResult>(`${BASE}/localize/${storyId}/accept-world`, body),

  // ── Cổng AI review (E3-P2) ─────────────────────────────────────────────
  /** Vẫn tiếp tục: BLOCKED ⇒ OVERRIDDEN, ghi người/lúc/lý do (≥ 5 ký tự ⇒ 422). Đồng bộ, không AI. */
  overrideGate: (storyId: string, stepKey: GateStepKey, reason: string) =>
    apiClient.post<{ gate: StepGate }>(`${BASE}/gates/${storyId}/${stepKey}/override`, { reason }),

  /** Chấm lại trạng thái hiện tại (sau khi sửa tay) — NỀN 202, KHÔNG chạy lại bước, KHÔNG tự sửa. */
  rejudgeGate: (storyId: string, stepKey: GateStepKey) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/gates/${storyId}/${stepKey}/rejudge`, {}),

  // ── "AI đọc cả mùa" (E3-P3) ────────────────────────────────────────────
  /** Trạng thái hiệu lực (đang đọc / cũ / chưa phủ tới tập M / …) — server tính vì cần băm kịch bản. Không AI. */
  seasonReaderView: (storyId: string) =>
    apiClient.get<SeasonReaderViewResponse>(`${BASE}/gates/${storyId}/season_reader`),

  /** Bấm tay "AI đọc cả mùa" — NỀN 202, khoá cùng việc viết kịch bản (đang viết ⇒ 409 busy). */
  runSeasonReader: (storyId: string) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/gates/${storyId}/season_reader/run`, {}),

  /** Vẫn tiếp tục: BLOCKED ⇒ OVERRIDDEN (lý do ≥ 5 ký tự ⇒ 422). Đồng bộ, không AI. */
  overrideSeasonReader: (storyId: string, reason: string) =>
    apiClient.post<{ gate: SeasonReaderGate }>(`${BASE}/gates/${storyId}/season_reader/override`, { reason }),

  /** Chỉ trích lại graph quan hệ nhân vật — nền. */
  runRelationships: (storyId: string) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/localize/relationships`, { storyId }),

  /** Sửa/khoá localizedName một nhân vật hoặc bối cảnh. */
  patchLocalizeEntity: (
    storyId: string,
    data: { kind: 'character' | 'location'; id: string; localizedName?: string; locked?: boolean },
  ) => apiClient.patch(`${BASE}/localize/entity`, { storyId, ...data }),

  // ── 261005 E3-P3a — sửa tay ③ (đường mới; `patchLocalizeEntity`/từ vựng cũ giữ cho client cũ, FE mới KHÔNG dùng để lưu tên/bối cảnh) ──
  previewLocalizeOps: (storyId: string, ops: LocalizeOp[]) =>
    apiClient.post<LocalizePreviewResponse>(`${BASE}/localize/${storyId}/preview`, { ops }),
  /** `expectedModifiedAt` BẮT BUỘC (lấy từ `baseModifiedAt` của xem thử). 409: OUTLINE_CHANGED · SCRIPT_CHANGED · HAS_ERRORS · JOB_RUNNING. */
  patchLocalizeOps: (storyId: string, ops: LocalizeOp[], expectedModifiedAt: string) =>
    apiClient.patch<LocalizePatchResponse>(`${BASE}/localize/${storyId}`, { ops, expectedModifiedAt }),
  localizeVersions: (storyId: string) =>
    apiClient.get<LocalizeVersionMeta[]>(`${BASE}/localize/${storyId}/versions`),
  localizeVersion: (storyId: string, versionId: string) =>
    apiClient.get<LocalizeVersionFull>(`${BASE}/localize/${storyId}/versions/${versionId}`),
  restoreLocalizeVersion: (storyId: string, versionId: string, expectedModifiedAt: string) =>
    apiClient.post<LocalizePatchResponse>(`${BASE}/localize/${storyId}/versions/${versionId}/restore`, { expectedModifiedAt }),

  addGlossary: (storyId: string, data: { originalTerm: string; localizedTerm: string; type?: FilmGlossaryType }) =>
    apiClient.post<FilmGlossaryTerm>(`${BASE}/localize/glossary`, { storyId, ...data }),

  updateGlossary: (id: string, patch: Partial<Pick<FilmGlossaryTerm, 'localizedTerm' | 'type' | 'note' | 'status'>>) =>
    apiClient.put<FilmGlossaryTerm>(`${BASE}/localize/glossary/${id}`, patch),

  deleteGlossary: (id: string) =>
    apiClient.delete(`${BASE}/localize/glossary/${id}`),

  approveAllGlossary: (storyId: string) =>
    apiClient.post<{ approved: number }>(`${BASE}/localize/glossary/approve-all`, { storyId }),

  // ── 261012 E1-P3 — nhận dạng hình ảnh (nhân vật / bối cảnh) ở chặng ② ──
  /** Tóm tắt + `profileView` từng thực thể + `limits.maxChars` + việc nền đang chạy. Không AI. */
  getVisualIdentity: (storyId: string) =>
    apiClient.get<VisualIdentitySummary>(`${BASE}/visual-identity/${storyId}`),
  /** TỐN TIỀN AI — mọi lời gọi phải đi qua `runConfirmCopy` (hộp ước giá). NỀN 202. 409 `JOB_RUNNING`. */
  runVisualIdentity: (body: VisualRunBody) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/visual-identity`, body),
  /** Sửa tay / Giữ / xoá một profile (CAS theo `baseVersion`). 409: PROFILE_CHANGED · LOCKED · JOB_RUNNING · OUTLINE_CHANGED. Không hạ duyệt khung xương. */
  patchVisualProfile: (body: VisualEditBody) =>
    apiClient.patch<VisualEditResult>(`${BASE}/visual-identity/entity`, body),

  // ── 261012 E1-P4 — người phụ không tên theo tập (chặng ⑤) ──
  /** Danh sách người phụ theo tập + `limits` + việc nền đang chạy. Không AI. */
  getVisualExtras: (storyId: string) =>
    apiClient.get<VisualExtrasSummary>(`${BASE}/visual-extras/${storyId}`),
  /** TỐN TIỀN AI — mọi lời gọi phải đi qua `runConfirmCopy` (hộp ước giá). NỀN 202. 409 `JOB_RUNNING`. */
  runVisualExtras: (body: VisualExtrasRunBody) =>
    apiClient.post<AdaptationBgStarted>(`${BASE}/visual-extras`, body),
  /** Thêm / sửa / Giữ / xoá một người phụ (CAS theo `baseVersion` của tập). 200 = `{episode, version, items}`; 409 `VERSION_CHANGED` kèm bản hiện tại. */
  patchVisualExtra: (body: VisualExtrasEditBody) =>
    apiClient.patch<VisualExtrasEditResult>(`${BASE}/visual-extras/entity`, body),
};

export type { FilmOutline };
